import { SUPER_ADMIN_TAG } from './permissions';
import { NAV_GROUPS } from './nav-sections.utils';

/**
 * Helpers for the permission management UI of "Administradores" (specs 131/132).
 * The catalog comes from `GET /useradmin/permissions/catalog`:
 * `{ permissions: [{ tag, label, kind, requires }],
 *    sections: [{ key, label, group, actions: [{ tag, label, kind, requires }] }],
 *    profiles: [{ key, label, permissions }] }`.
 * Every helper is pure: it returns a new tag list and never mutates its input.
 */

/** Fallback label for `*`, used only if the catalog does not list it. */
export const SUPER_ADMIN_LABEL = 'Acceso total (Super admin)';

/**
 * Permission groups in menu order (spec 132). `group` slugs come from the catalog.
 * Since 2026-11-03 there is no shared group: an action used by several screens
 * is repeated in the section of each one.
 */
export const PERMISSION_GROUPS = [
  { key: 'metrics', title: 'Métricas' },
  { key: 'general', title: 'General' },
  { key: 'revenue', title: 'Revenue' },
  { key: 'vetops', title: 'VetOps' },
  { key: 'operations', title: 'Operaciones' },
  { key: 'finance', title: 'Finanzas' },
  { key: 'maintainers', title: 'Mantenedores' },
  { key: 'internal-tools', title: 'Herramientas Internas' }
];

/** Spanish tag shown next to each action, by catalog `kind`. */
export const ACTION_KIND_LABELS = {
  view: 'Ver',
  create: 'Crear',
  update: 'Editar',
  delete: 'Eliminar',
  export: 'Exportar',
  special: 'Acción especial'
};

/**
 * Label of `*`, taken from the catalog entry when present.
 * @param {{ permissions?: { tag: string, label: string }[] }|null} catalog
 * @returns {string}
 */
export function getSuperAdminLabel(catalog) {
  const entry = ((catalog && catalog.permissions) || []).find((e) => e.tag === SUPER_ADMIN_TAG);
  return (entry && entry.label) || SUPER_ADMIN_LABEL;
}

function sameTags(a, b) {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((tag) => right.has(tag));
}

/**
 * Profile whose tag list is exactly `permissions`, ignoring order and duplicates.
 * @param {string[]} permissions
 * @param {{ profiles?: { key: string, label: string, permissions: string[] }[] }|null} catalog
 * @returns {{ key: string, label: string, permissions: string[] }|null}
 */
export function findMatchingProfile(permissions, catalog) {
  const profiles = (catalog && catalog.profiles) || [];
  return profiles.find((profile) => sameTags(profile.permissions || [], permissions || [])) || null;
}

/**
 * One-line summary of an admin's stored tags for the "Administradores" list
 * (spec 132): the profile label when the list matches a profile exactly,
 * otherwise "Personalizado (N)" with N = stored tags. `null` when there are none.
 * @param {string[]} permissions
 * @param {object|null} catalog
 * @returns {string|null}
 */
export function summarizePermissions(permissions, catalog) {
  const list = Array.isArray(permissions) ? [...new Set(permissions)] : [];
  if (!list.length) return null;
  const profile = findMatchingProfile(list, catalog);
  return profile ? profile.label : `Personalizado (${list.length})`;
}

/**
 * Every assignable action of the catalog, keyed by tag (without `*`).
 * Taken from `sections` (spec 132), or from the flat `permissions` list.
 * @param {object|null} catalog
 * @returns {Map<string, { tag: string, label: string, kind?: string, requires?: string|null }>}
 */
export function getActionsByTag(catalog) {
  const sections = (catalog && catalog.sections) || [];
  const fromSections = sections.flatMap((section) => section.actions || []);
  const entries = fromSections.length ? fromSections : (catalog && catalog.permissions) || [];
  return new Map(
    entries.filter((entry) => entry.tag !== SUPER_ADMIN_TAG).map((entry) => [entry.tag, entry])
  );
}

/** Whether `list` grants `tag`: listed, or `*` (which grants every tag). */
function grants(list, tag) {
  return list.includes(tag) || list.includes(SUPER_ADMIN_TAG);
}

/** Suffix of the tag that narrows an action to the person's own records. */
const OWN_SUFFIX = '-own';

/**
 * Tags that satisfy a `requires` R: R itself and, when the catalog has it,
 * its narrower `R-own` (spec 132, Phase 12: "Ver sus propios registros"
 * satisfies the "Ver" that Crear/Editar/Eliminar require).
 * @param {string} required
 * @param {Map<string, object>} actions - from `getActionsByTag`.
 * @returns {string[]}
 */
function requirementVariants(required, actions) {
  const own = `${required}${OWN_SUFFIX}`;
  return actions.has(own) ? [required, own] : [required];
}

/**
 * Whether `tag` is the general action of an own/general pair (its `-own`
 * variant is also in the catalog), e.g. `service-records:update`.
 * @param {string} tag
 * @param {Map<string, object>} actions - from `getActionsByTag`.
 * @returns {boolean}
 */
function isScopedGeneral(tag, actions) {
  return !tag.endsWith(OWN_SUFFIX) && actions.has(`${tag}${OWN_SUFFIX}`);
}

/**
 * Whether `list` satisfies the `requires` R of `action`: R, `R-own` (when in
 * the catalog) or `*`. Scopes are correlative: the general action of a pair
 * ("Editar todos") needs the general R ("Ver todos") itself. Mirrors the
 * backend `findMissingRequiredPermissions`.
 * @param {string[]} list
 * @param {string} required
 * @param {Map<string, object>} actions - from `getActionsByTag`.
 * @param {string} [tag] - The action that needs R.
 * @returns {boolean}
 */
function satisfiesRequirement(list, required, actions, tag) {
  if (tag && isScopedGeneral(tag, actions)) return grants(list, required);
  return requirementVariants(required, actions).some((variant) => grants(list, variant));
}

/**
 * Whether the requirement R of an action can be met by `tag`
 * (`tag` is R, or `tag` is the `R-own` variant of R).
 */
function isVariantOf(tag, required, actions) {
  return requirementVariants(required, actions).includes(tag);
}

/**
 * Adds the `requires` of every tag (transitively) when it is not satisfied yet.
 * A requirement R is satisfied by R, by its `R-own` variant (when the catalog
 * has it) or by `*`; when missing, the plain R is added.
 * Mirrors the backend rule that answers MISSING_VIEW_PERMISSION (spec 132).
 * @param {string[]} tags
 * @param {object|null} catalog
 * @returns {string[]}
 */
export function withRequired(tags, catalog) {
  const actions = getActionsByTag(catalog);
  const result = [...new Set(tags || [])];
  for (let i = 0; i < result.length; i += 1) {
    const action = actions.get(result[i]);
    const required = action && action.requires;
    if (required && !satisfiesRequirement(result, required, actions, result[i])) result.push(required);
  }
  return result;
}

/**
 * Removes `tag` and every tag that requires it, directly or transitively
 * (unchecking "Ver" unchecks the section's actions). A dependent stays while
 * another variant still satisfies its requirement: removing "Ver todos" keeps
 * the actions when "Ver sus propios" is still on, and removing the last Ver
 * variant clears them.
 * @param {string[]} tags
 * @param {string} tag
 * @param {object|null} catalog
 * @returns {string[]}
 */
export function withoutDependents(tags, tag, catalog) {
  const actions = getActionsByTag(catalog);
  const removed = new Set([tag]);
  const remaining = () => (tags || []).filter((t) => !removed.has(t));
  const isBroken = (action) =>
    Boolean(action.requires) &&
    !removed.has(action.tag) &&
    [...removed].some((gone) => isVariantOf(gone, action.requires, actions)) &&
    !satisfiesRequirement(remaining(), action.requires, actions, action.tag);
  let grew = true;
  while (grew) {
    grew = false;
    actions.forEach((action) => {
      if (isBroken(action)) {
        removed.add(action.tag);
        grew = true;
      }
    });
  }
  return remaining();
}

/**
 * Checks or unchecks one tag, keeping the `requires` rule.
 * @param {string[]} tags
 * @param {string} tag
 * @param {boolean} checked
 * @param {object|null} catalog
 * @returns {string[]}
 */
export function toggleTag(tags, tag, checked, catalog) {
  return checked ? withRequired([...(tags || []), tag], catalog) : withoutDependents(tags, tag, catalog);
}

/**
 * Tags of every action of a section.
 * @param {{ actions?: object[] }} section
 * @returns {string[]}
 */
export function rowTags(section) {
  return ((section && section.actions) || []).map((action) => action.tag);
}

/**
 * Tags of every section of a group.
 * @param {object|null} catalog
 * @param {string} groupKey
 * @returns {string[]}
 */
export function groupTags(catalog, groupKey) {
  return ((catalog && catalog.sections) || [])
    .filter((section) => section.group === groupKey)
    .flatMap((section) => rowTags(section));
}

/**
 * Checks or unchecks several tags at once ("Activar todas" / "Quitar todas").
 * @param {string[]} tags
 * @param {string[]} targets
 * @param {boolean} checked
 * @param {object|null} catalog
 * @returns {string[]}
 */
export function setTags(tags, targets, checked, catalog) {
  if (checked) return withRequired([...(tags || []), ...targets], catalog);
  return targets.reduce((list, tag) => withoutDependents(list, tag, catalog), tags || []);
}

/**
 * How many of `targets` the list grants (`*` grants all) and the resulting
 * status for the section/group indicators: 'none', 'partial' or 'full'.
 * A target can be a tag or a list of alternative tags (a scoped pair, see
 * `sectionUnits`), counted once and active when any of them is granted.
 * An empty `targets` list is 'none'.
 * @param {string[]} list
 * @param {(string|string[])[]} targets
 * @returns {{ active: number, total: number, status: 'none'|'partial'|'full' }}
 */
export function grantSummary(list, targets) {
  const isGranted = (target) =>
    (Array.isArray(target) ? target : [target]).some((tag) => grants(list || [], tag));
  const total = (targets || []).length;
  const active = (targets || []).filter(isGranted).length;
  let status = 'partial';
  if (!active) status = 'none';
  else if (active === total) status = 'full';
  return { active, total, status };
}

const LEAF_ORDER = new Map(
  NAV_GROUPS.flatMap((group) => group.subLinks || []).map((leaf, index) => [leaf.key, index])
);

function leafIndex(section) {
  return LEAF_ORDER.has(section.key) ? LEAF_ORDER.get(section.key) : LEAF_ORDER.size;
}

/**
 * Catalog sections grouped by menu group, groups and rows in menu order.
 * Unknown groups go last with their slug as title. Empty groups are dropped.
 * @param {object|null} catalog
 * @returns {{ key: string, title: string, sections: object[] }[]}
 */
/**
 * Keeps only the tags the catalog still knows (plus `*`). An admin may hold a
 * tag that was removed from the catalog (e.g. `customers:lookup`); sending it
 * back would make the backend reject the whole save with UNKNOWN_PERMISSION.
 * @param {string[]} tags
 * @param {{ permissions?: { tag: string }[] }} catalog
 * @returns {string[]}
 */
export function knownTags(tags, catalog) {
  const known = new Set(((catalog && catalog.permissions) || []).map((entry) => entry.tag));
  known.add(SUPER_ADMIN_TAG);
  return (tags || []).filter((tag) => known.has(tag));
}

export function getMatrixGroups(catalog) {
  const sections = (catalog && catalog.sections) || [];
  const known = PERMISSION_GROUPS.map((group) => group.key);
  const unknown = [...new Set(sections.map((s) => s.group))].filter((g) => !known.includes(g));
  const groups = [...PERMISSION_GROUPS, ...unknown.map((key) => ({ key, title: key }))];
  return groups
    .map((group) => ({
      ...group,
      sections: sections
        .filter((section) => section.group === group.key)
        .map((section, index) => ({ section, index }))
        .sort((a, b) => leafIndex(a.section) - leafIndex(b.section) || a.index - b.index)
        .map(({ section }) => section)
    }))
    .filter((group) => group.sections.length > 0);
}

/**
 * Spanish labels of tags, for error messages (e.g. MISSING_VIEW_PERMISSION).
 * @param {string[]} tags
 * @param {object|null} catalog
 * @returns {string[]}
 */
export function tagLabels(tags, catalog) {
  const actions = getActionsByTag(catalog);
  return (tags || []).map((tag) => (actions.get(tag) && actions.get(tag).label) || tag);
}

/**
 * Splits a section for the detail pane: its "Ver" action (kind `view`), the
 * narrower `Ver-own` variant when the section has one ("Ver sus propios",
 * spec 132 Phase 12), the actions that require Ver, and the actions granted on
 * their own (sections without "Ver", or actions whose `requires` is not that
 * "Ver").
 * @param {{ actions?: object[] }} section
 * @returns {{ view: object|null, viewOwn: object|null, dependents: object[], independent: object[] }}
 */
export function splitSectionActions(section) {
  const actions = (section && section.actions) || [];
  const views = actions.filter((action) => action.kind === 'view');
  const viewTags = new Set(views.map((action) => action.tag));
  const isOwnVariant = (action) =>
    action.tag.endsWith(OWN_SUFFIX) && viewTags.has(action.tag.slice(0, -OWN_SUFFIX.length));
  const view = views.find((action) => !isOwnVariant(action)) || null;
  const viewOwn = (view && views.find((action) => action.tag === `${view.tag}${OWN_SUFFIX}`)) || null;
  const others = actions.filter((action) => action !== view && action !== viewOwn);
  const viewVariants = [view, viewOwn].filter(Boolean).map((action) => action.tag);
  const byTag = new Map(actions.map((action) => [action.tag, action]));
  // Follows the `requires` chain inside the section: "Ver pagos" → "Ver membresías" → "Ver".
  const dependsOnView = (action, seen = new Set()) => {
    if (viewVariants.includes(action.requires)) return true;
    const required = byTag.get(action.requires);
    if (!required || seen.has(required.tag)) return false;
    seen.add(required.tag);
    return dependsOnView(required, seen);
  };
  return {
    view,
    viewOwn,
    dependents: others.filter((action) => dependsOnView(action)),
    independent: others.filter((action) => !dependsOnView(action))
  };
}

/**
 * Whether the section's "Ver" is on in any variant ("Todos" or "Solo los
 * propios"), or through `*`.
 * @param {string[]} tags
 * @param {{ view: object|null, viewOwn?: object|null }} split - from `splitSectionActions`.
 * @returns {boolean}
 */
export function isViewOn(tags, split) {
  const list = tags || [];
  return [split.view, split.viewOwn].some((action) => Boolean(action) && grants(list, action.tag));
}

/**
 * Spanish tag for an action kind ("Crear", "Exportar"...); unknown kinds
 * fall back to "Acción especial".
 * @param {string} kind
 * @returns {string}
 */
export function actionKindLabel(kind) {
  return ACTION_KIND_LABELS[kind] || ACTION_KIND_LABELS.special;
}

/**
 * Muted helper line under an action switch. Built only from the catalog data
 * (kind, `requires`, the `-own` suffix of the tag), so it never states facts
 * the catalog does not carry. When `requires` is another action of the same
 * section (not its Ver), that action is named.
 * @param {{ tag: string, kind?: string, requires?: string|null }} action
 * @param {object[]} [sectionActions] - actions of the action's section.
 * @returns {string}
 */
export function actionHelper(action, sectionActions = []) {
  if (action.kind === 'view') {
    return 'Sin Ver no se puede usar ninguna de las acciones de esta sección.';
  }
  const notes = [];
  if (/-own$/.test(action.tag)) notes.push('Solo los registros que creó esta persona');
  if (action.kind === 'export') notes.push('Genera un archivo descargable');
  const required = sectionActions.find((other) => other.tag === action.requires);
  if (required && required.kind !== 'view') notes.push(`Requiere "${required.label}"`);
  else notes.push(action.requires ? 'Requiere Ver' : 'No requiere Ver');
  return notes.join(' · ');
}

/** Values of the 3-option control of a scoped pair. */
export const SCOPES = { none: 'none', own: 'own', all: 'all' };

/**
 * Neutral Spanish name for a scoped pair: the own label without "sus propios"
 * ("Editar sus propios registros de servicio" → "Editar registros de servicio"),
 * else the general label without "cualquier(a)", else "<Kind> (alcance)".
 * @param {{ label: string, kind?: string }} general
 * @param {{ label: string }} own
 * @returns {string}
 */
export function scopedPairLabel(general, own) {
  const fromOwn = (own.label || '').replace(/ sus propi[oa]s\b/i, '');
  if (fromOwn !== own.label) return fromOwn;
  const fromGeneral = (general.label || '').replace(/ cualquiera?\b/i, '');
  if (fromGeneral !== general.label) return fromGeneral;
  return `${actionKindLabel(general.kind)} (alcance)`;
}

/**
 * Rows of the detail pane for a list of actions: an action `X` and its
 * narrower `X-own` in the same list collapse into one scoped pair row, placed
 * where `X` is; every other action is its own row.
 * @param {object[]} actions
 * @returns {({ key: string, action: object }|{ key: string, pair: { general: object, own: object, label: string, kind?: string, requires?: string|null } })[]}
 */
export function pairScopedActions(actions) {
  const list = actions || [];
  const byTag = new Map(list.map((action) => [action.tag, action]));
  const pairedOwn = new Set(
    list
      .filter((action) => action.tag.endsWith(OWN_SUFFIX))
      .filter((action) => byTag.has(action.tag.slice(0, -OWN_SUFFIX.length)))
      .map((action) => action.tag)
  );
  return list
    .filter((action) => !pairedOwn.has(action.tag))
    .map((action) => {
      const ownTag = `${action.tag}${OWN_SUFFIX}`;
      if (!pairedOwn.has(ownTag)) return { key: action.tag, action };
      const own = byTag.get(ownTag);
      const pair = {
        general: action,
        own,
        label: scopedPairLabel(action, own),
        kind: action.kind,
        requires: action.requires
      };
      return { key: action.tag, pair };
    });
}

/** Tags of one detail-pane row: `[tag]`, or `[general, own]` for a pair. */
function rowUnit(row) {
  return row.pair ? [row.pair.general.tag, row.pair.own.tag] : [row.action.tag];
}

/**
 * Counting units of a list of actions: `[tag]` per action, `[general, own]`
 * per scoped pair (counted as one action by `grantSummary`).
 * @param {object[]} actions
 * @returns {string[][]}
 */
export function actionUnits(actions) {
  return pairScopedActions(actions).map(rowUnit);
}

/**
 * Counting units of a section (see `actionUnits`).
 * @param {{ actions?: object[] }} section
 * @returns {string[][]}
 */
export function sectionUnits(section) {
  return actionUnits((section && section.actions) || []);
}

/**
 * Every scoped pair of the catalog's sections.
 * @param {object|null} catalog
 * @returns {object[]}
 */
export function getScopedPairs(catalog) {
  return ((catalog && catalog.sections) || []).flatMap((section) =>
    pairScopedActions(section.actions)
      .filter((row) => row.pair)
      .map((row) => row.pair)
  );
}

/**
 * Current scope of a pair: 'all' when the general tag is granted (or `*`,
 * and also when both tags are stored), 'own' for the own tag alone, else 'none'.
 * @param {string[]} tags
 * @param {{ general: { tag: string }, own: { tag: string } }} pair
 * @returns {'none'|'own'|'all'}
 */
export function scopeOf(tags, pair) {
  const list = tags || [];
  if (grants(list, pair.general.tag)) return SCOPES.all;
  return list.includes(pair.own.tag) ? SCOPES.own : SCOPES.none;
}

/**
 * Sets the scope of a pair: 'all' keeps only the general tag, 'own' only the
 * own tag, 'none' neither. Adds `requires` ("Ver") like `toggleTag`. Switching
 * between 'all' and 'own' keeps the actions that require the pair (either
 * variant satisfies them); 'none' clears them once no variant is left.
 * @param {string[]} tags
 * @param {{ general: { tag: string }, own: { tag: string } }} pair
 * @param {'none'|'own'|'all'} scope
 * @param {object|null} catalog
 * @returns {string[]}
 */
export function setScope(tags, pair, scope, catalog) {
  const pairTags = [pair.general.tag, pair.own.tag];
  // Narrowing "Ver" to the own records narrows the pairs that depend on it too.
  const list = scope === SCOPES.own ? narrowDependentScopes(tags || [], pair.general.tag, catalog) : tags || [];
  if (scope === SCOPES.all || scope === SCOPES.own) {
    const chosen = scope === SCOPES.all ? pair.general.tag : pair.own.tag;
    return withRequired([...list.filter((tag) => !pairTags.includes(tag)), chosen], catalog);
  }
  return pairTags.reduce((next, tag) => withoutDependents(next, tag, catalog), list);
}

/**
 * Replaces every general pair action that requires `requiredTag` with its own
 * variant ("Editar todos" → "Editar sus propios") when "Ver" is narrowed.
 * @param {string[]} tags
 * @param {string} requiredTag
 * @param {object|null} catalog
 * @returns {string[]}
 */
function narrowDependentScopes(tags, requiredTag, catalog) {
  const narrowed = new Map(
    getScopedPairs(catalog)
      .filter((pair) => pair.general.requires === requiredTag && tags.includes(pair.general.tag))
      .map((pair) => [pair.general.tag, pair.own.tag])
  );
  return [...new Set(tags.map((tag) => narrowed.get(tag) || tag))];
}

/**
 * Whether a pair may be set to "Todos": not while its required "Ver" is only
 * the own variant (seeing only your own records never allows acting on
 * everyone's). With no Ver at all "Todos" stays available and adds Ver.
 * @param {string[]} tags
 * @param {{ general: { tag: string, requires?: string|null } }} pair
 * @param {object|null} catalog
 * @returns {boolean}
 */
export function canScopeAll(tags, pair, catalog) {
  const required = pair.general.requires;
  if (!required) return true;
  const list = tags || [];
  if (grants(list, required)) return true;
  const actions = getActionsByTag(catalog);
  const own = `${required}${OWN_SUFFIX}`;
  return !(actions.has(own) && list.includes(own));
}

/**
 * Drops each `X-own` tag whose general `X` is also listed: the general tag
 * already covers the person's own records.
 * @param {string[]} tags
 * @param {object|null} catalog
 * @returns {string[]}
 */
export function withoutRedundantOwn(tags, catalog) {
  const list = tags || [];
  const redundant = new Set(
    getScopedPairs(catalog)
      .filter((pair) => list.includes(pair.general.tag))
      .map((pair) => pair.own.tag)
  );
  return list.filter((tag) => !redundant.has(tag));
}

/** Subsection titles of the detail pane, by action kind, in display order. */
export const KIND_SUBSECTIONS = [
  { kind: 'view', label: 'Ver' },
  { kind: 'create', label: 'Crear' },
  { kind: 'update', label: 'Editar' },
  { kind: 'delete', label: 'Eliminar' },
  { kind: 'export', label: 'Exportar' },
  { kind: 'special', label: 'Otras acciones' }
];

/**
 * Kind under which a `special` action is listed in the detail pane, keyed by
 * tag: these actions are a narrower case of seeing, creating, editing or
 * deleting. Display only: the action keeps its own tag and `requires`, so it
 * is shown as a sibling row, not nested under the kind's main action.
 * Drill-down tags (`<section>:view-<detail>`) need no entry: `specialParentKind`
 * lists them under "Ver" by name. Other special actions not listed here stay
 * in "Otras acciones".
 */
export const SPECIAL_PARENT_KIND = {
  'memberships:filter-by-contract': 'view',
  'users:add-pet': 'create',
  'users:create-co-tutors': 'create',
  'users:edit-pet': 'update',
  'users:update-memberships': 'update',
  'users:change-membership-status': 'update',
  'users:renew-memberships': 'update',
  'users:regenerate-payment-link': 'update',
  'users:manage-sponsorship': 'update',
  'users:update-co-tutors': 'update',
  'users:flag-suspicious': 'update',
  'users:delete-pet': 'delete',
  'quote-builder:generate-proposal': 'create',
  'refund-inbox:import-payments': 'create',
  'bulk-uploads:import': 'create',
  'bulk-uploads:sync-payroll': 'update',
  'clinic-billing:import-payments': 'create',
  'mrr-billing-import:import': 'create',
  'mrr-breakdown:create-billing': 'create',
  'mrr-new:create-billing': 'create',
  'terms-notification:send': 'create',
  'contracts:manage-documents': 'update',
  'plans:manage-services': 'update',
  'plans:manage-benefits': 'update',
  'plans:manage-features': 'update',
  'plans:manage-alliance-exclusions': 'update',
  'users:reset-password': 'update',
  'users:force-logout': 'update',
  'users:blacklist': 'update',
  'users:validate-identity': 'update',
  'users:manage-company-access': 'update',
  'pets:manage-records': 'update',
  'memberships:change-status': 'update',
  'arco-requests:resolve': 'update',
  'b2b-renewals:renew': 'update',
  'quotes:send-proposal': 'update',
  'refund-inbox:review-status': 'update',
  'register-refund:update-bank-data': 'update',
  'droopy-clinics:toggle-status': 'update',
  'droopy-clinics:update-bank-details': 'update',
  'droopy-clinics:manage-services': 'update',
  'droopy-clinics:reset-password': 'update',
  'contract-renewal:execute': 'update',
  'pricing:approve': 'update',
  'clinic-billing:cancel-attendance': 'update',
  'admins:reset-mfa': 'update',
  'admins:manage-permissions': 'update',
  'admins:change-status': 'update',
  'mrr-breakdown:update-billing': 'update',
  'refund-inbox:flag-suspicious': 'update',
  'droopy-clinics:flag-suspicious': 'update',
  'bulk-uploads:deactivate': 'delete',
  'plans:remove-services': 'delete',
  'mrr-breakdown:delete-billing': 'delete'
};

// `<section>:view-<detail>`: a drill-down of what the section's Ver shows.
const DRILL_DOWN_TAG = /^[a-z0-9-]+:view-[a-z0-9-]+$/;

/**
 * Kind a special action is listed under, or `null`: its `SPECIAL_PARENT_KIND`
 * entry, or "Ver" for a drill-down tag.
 * @param {{ tag: string, kind?: string }} action
 * @returns {string|null}
 */
export function specialParentKind(action) {
  if (!action || action.kind !== 'special') return null;
  if (SPECIAL_PARENT_KIND[action.tag]) return SPECIAL_PARENT_KIND[action.tag];
  return DRILL_DOWN_TAG.test(action.tag) ? 'view' : null;
}

/** Subsection of a main row: its kind, or 'special' for kinds without a subsection. */
function rowKind(row) {
  const kind = row.pair ? row.pair.kind : row.action.kind;
  return KIND_SUBSECTIONS.some((entry) => entry.kind === kind) ? kind : 'special';
}

/** The action a row stands for: the action itself, or the general action of a scoped pair. */
const rowAction = (row) => row.action || row.pair.general;

/** Whether a row is a special action (or scoped pair of them) listed under another kind. */
function isMappedSpecial(row) {
  return Boolean(specialParentKind(rowAction(row)));
}

const rowTag = (row) => (row.pair ? row.pair.general.tag : row.action.tag);
const rowRequires = (row) => (row.pair ? row.pair.requires : row.action.requires);

/**
 * Puts each row right after the row it requires and sets its `depth`, so a
 * dependency reads as indentation: in "Ver", the narrower Ver actions sit
 * under the section's Ver, and "Ver pagos" one level further under "Ver
 * membresías". A row whose requirement is in another subsection stays at 0.
 * @param {object[]} rows - rows of one kind subsection, in catalog order.
 * @returns {object[]} the same rows with `depth`, parents before children.
 */
function nestByRequires(rows) {
  const parentTags = new Set(rows.map(rowTag));
  const isChild = (row) => parentTags.has(rowRequires(row));
  const nested = [];
  const visit = (row, depth) => {
    nested.push({ ...row, depth });
    rows.filter((other) => isChild(other) && rowRequires(other) === rowTag(row)).forEach((child) => visit(child, depth + 1));
  };
  rows.filter((row) => !isChild(row)).forEach((row) => visit(row, 0));
  return nested;
}

/**
 * Actions of a section grouped by kind for the detail pane, in the order of
 * `KIND_SUBSECTIONS` ("Ver" first, with the section's Ver action as its first
 * row). Scoped pairs `X` + `X-own` are one row. Special actions mapped by
 * `SPECIAL_PARENT_KIND` go after the kind's own actions as plain rows: they
 * do not depend on them. A row that requires another row of the subsection
 * goes right after it with a higher `depth` (see `nestByRequires`). Kinds with
 * nothing are left out. `units` feeds `grantSummary` (pairs count once).
 * @param {{ actions?: object[] }} section
 * @returns {{ kind: string, label: string, rows: object[], units: string[][] }[]}
 */
export function groupActionsByKind(section) {
  const rows = pairScopedActions((section && section.actions) || []);
  const ownRows = rows.filter((row) => !isMappedSpecial(row));
  const mappedRows = rows.filter(isMappedSpecial);
  return KIND_SUBSECTIONS.map(({ kind, label }) => {
    const kindRows = nestByRequires([
      ...ownRows.filter((row) => rowKind(row) === kind),
      ...mappedRows.filter((row) => specialParentKind(rowAction(row)) === kind)
    ]);
    return { kind, label, rows: kindRows, units: kindRows.map(rowUnit) };
  }).filter((group) => group.rows.length > 0);
}

/**
 * Helper line under a scoped pair, consistent with `actionHelper`. The "Ver"
 * pair of a section says that without Ver no action can be used.
 * @param {{ kind?: string, requires?: string|null }} pair
 * @returns {string}
 */
export function scopedPairHelper(pair) {
  if (pair.kind === 'view') {
    return 'Todos incluye también los registros propios · Sin Ver no se puede usar ninguna de las acciones de esta sección.';
  }
  const requires = pair.requires ? 'Requiere Ver' : 'No requiere Ver';
  return `Todos incluye también los registros propios · ${requires}`;
}

/**
 * Generic one-line description of a section for the detail pane.
 * @param {{ key: string, title: string }} group - group from `getMatrixGroups`.
 * @returns {string}
 */
export function sectionDescription(group) {
  return group ? `Sección del menú ${group.title} del panel de administración.` : '';
}

/**
 * Number of active permissions for the dialog footer: every assignable action
 * of the catalog when the list has `*` (a scoped pair counts once), otherwise
 * the distinct stored tags.
 * @param {string[]} tags
 * @param {object|null} catalog
 * @returns {number}
 */
export function countActivePermissions(tags, catalog) {
  const list = [...new Set(tags || [])];
  if (list.includes(SUPER_ADMIN_TAG)) {
    return getActionsByTag(catalog).size - getScopedPairs(catalog).length;
  }
  return list.length;
}

/**
 * Number of tags added or removed between two lists (unsaved changes).
 * @param {string[]} before
 * @param {string[]} after
 * @returns {number}
 */
export function countChanges(before, after) {
  const left = new Set(before || []);
  const right = new Set(after || []);
  const added = [...right].filter((tag) => !left.has(tag)).length;
  const removed = [...left].filter((tag) => !right.has(tag)).length;
  return added + removed;
}
